import { useEffect, useState } from "react";
import axios from "axios";
import { LoaderCircle, MoveRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  full_name: "",
  email: "",
  current_role: "",
  exploration_note: "",
};

export default function LeadCaptureDialog({ open, onOpenChange }) {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setFormData(initialForm);
        setIsSubmitting(false);
        setIsSubmitted(false);
      }, 180);
    }
  }, [open]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/discovery-request`, formData);
      toast.success(response.data.message);
      setIsSubmitted(true);
    } catch (error) {
      const fallbackMessage = "Something went wrong while saving your request.";
      const apiMessage = error.response?.data?.detail;
      toast.error(Array.isArray(apiMessage) ? fallbackMessage : apiMessage || fallbackMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl border-none bg-transparent p-0 shadow-none"
        data-testid="discovery-modal"
      >
        <div className="modal-panel" data-testid="discovery-modal-panel">
          <DialogHeader className="modal-header" data-testid="discovery-modal-header">
            <span className="modal-route" data-testid="discovery-modal-route">
              /discover-your-pivot
            </span>
            <DialogTitle className="section-title section-title--medium !mt-0" data-testid="discovery-modal-title">
              Take the Free Founder Signal Test
            </DialogTitle>
            <DialogDescription className="modal-copy !text-[1rem]" data-testid="discovery-modal-description">
              Start with a short founder intake. Your responses help Pivotbox understand where you are in the decision journey.
            </DialogDescription>
          </DialogHeader>

          {isSubmitted ? (
            <div className="modal-grid" data-testid="discovery-modal-success-state">
              <div className="modal-success" data-testid="discovery-modal-success-message">
                Your discovery request has been recorded. We now have the context needed to guide you into the Founder Signal Test experience.
              </div>
              <div className="modal-actions" data-testid="discovery-modal-success-actions">
                <button
                  className="pivotbox-button"
                  data-testid="discovery-modal-close-button"
                  onClick={() => onOpenChange(false)}
                  type="button"
                >
                  Return to Pivotbox
                </button>
              </div>
            </div>
          ) : (
            <form className="modal-grid" data-testid="discovery-form" onSubmit={handleSubmit}>
              <div data-testid="discovery-form-name-field">
                <label className="field-label" data-testid="discovery-form-name-label" htmlFor="full_name">
                  Full name
                </label>
                <Input
                  className="pivotbox-input"
                  data-testid="discovery-form-name-input"
                  id="full_name"
                  name="full_name"
                  onChange={updateField}
                  placeholder="Your name"
                  required
                  value={formData.full_name}
                />
              </div>

              <div data-testid="discovery-form-email-field">
                <label className="field-label" data-testid="discovery-form-email-label" htmlFor="email">
                  Email address
                </label>
                <Input
                  className="pivotbox-input"
                  data-testid="discovery-form-email-input"
                  id="email"
                  name="email"
                  onChange={updateField}
                  placeholder="you@company.com"
                  required
                  type="email"
                  value={formData.email}
                />
              </div>

              <div data-testid="discovery-form-role-field">
                <label className="field-label" data-testid="discovery-form-role-label" htmlFor="current_role">
                  Current role or professional context
                </label>
                <Input
                  className="pivotbox-input"
                  data-testid="discovery-form-role-input"
                  id="current_role"
                  name="current_role"
                  onChange={updateField}
                  placeholder="For example: Product leader, consultant, operator"
                  required
                  value={formData.current_role}
                />
              </div>

              <div data-testid="discovery-form-note-field">
                <label className="field-label" data-testid="discovery-form-note-label" htmlFor="exploration_note">
                  What is pulling you toward entrepreneurship right now?
                </label>
                <Textarea
                  className="pivotbox-textarea min-h-[140px]"
                  data-testid="discovery-form-note-input"
                  id="exploration_note"
                  name="exploration_note"
                  onChange={updateField}
                  placeholder="Share a little about what you are evaluating or wrestling with."
                  required
                  value={formData.exploration_note}
                />
              </div>

              <div className="modal-actions" data-testid="discovery-form-actions">
                <button
                  className="pivotbox-button"
                  data-testid="discovery-form-submit-button"
                  disabled={isSubmitting}
                  type="submit"
                >
                  {isSubmitting ? (
                    <>
                      <LoaderCircle className="animate-spin" size={18} />
                      Saving your request
                    </>
                  ) : (
                    <>
                      Continue
                      <MoveRight size={18} />
                    </>
                  )}
                </button>

                <button
                  className="pivotbox-button pivotbox-button--ghost"
                  data-testid="discovery-form-cancel-button"
                  onClick={() => onOpenChange(false)}
                  type="button"
                >
                  Not now
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}